import { useState, useEffect, useCallback } from 'react';
import { useHomes } from './useHomes';

interface HomeData {
  chat_name: string;
  details: string;
}

export function useHomeDetails(homeId: string) {
  const [homeData, setHomeData] = useState<HomeData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { updateHome, isSubmitting } = useHomes();

  const fetchHomeDetails = useCallback(async () => {
    if (!homeId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/homes/${homeId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch home details: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setHomeData({
          chat_name: data.chat_name,
          details: data.details
        });
      } else {
        throw new Error(data.error || 'Failed to fetch home details');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching home details:', err);
    } finally {
      setLoading(false);
    }
  }, [homeId]);

  // Wrapper for updateHome that updates local state after successful update
  const editHome = useCallback(async (
    homeName: string, 
    address: string, 
    details: {
      bedrooms: number;
      bathrooms: number;
      sqft: number;
      price: number;
    }
  ) => {
    const result = await updateHome(homeId, homeName, address, details);
    
    if (result.success) {
      // Update local state with new data - no need to refetch
      setHomeData({
        chat_name: homeName,
        details: result.updatedDetails || JSON.stringify({
          address,
          bedrooms: details.bedrooms,
          bathrooms: details.bathrooms,
          sqft: details.sqft
        })
      });
      return { success: true };
    } else {
      setError(result.error || 'Failed to update home details');
      return { success: false, error: result.error };
    }
  }, [homeId, updateHome]);

  // Fetch home details on mount or homeId change
  useEffect(() => {
    fetchHomeDetails();
  }, [fetchHomeDetails]);

  return {
    homeData,
    loading,
    error,
    isSubmitting,
    editHome,
    refreshHomeDetails: fetchHomeDetails,
    setError
  };
}